<template>
  <b-modal
    v-model="show"
    :title="form.id ? 'Edit Question' : 'New Question'"
    size="lg"
    ok-title="Save"
    :ok-disabled="saving"
    no-close-on-backdrop
    @ok="handleSave"
  >
    <b-overlay :show="saving">
      <b-form @submit.prevent>
        <b-alert v-if="locked" show variant="secondary" class="p-1">
          <span v-if="form.isSystem">This is a system question: its input type and scope are fixed.</span>
          <span v-else>
            This question is used by {{ plural(form.usageCount, 'package item') }}, so its input type and
            scope are locked; the label, help text, options and defaults can still be changed.
          </span>
        </b-alert>

        <b-form-group label="Label">
          <b-form-input v-model="form.label" placeholder="The question as shown to the client" autofocus />
          <small v-if="form.key" class="text-muted">Key <code>{{ form.key }}</code></small>
        </b-form-group>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Help text">
              <b-form-input v-model="form.help" placeholder="Optional, shown under the field" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Placeholder">
              <b-form-input v-model="form.placeholder" placeholder="Optional, shown inside the empty field" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Input type">
              <b-form-select v-model="form.inputType" :options="inputTypeOptions" :disabled="locked" />
              <small v-if="locked" class="text-muted">Locked while the question is in use.</small>
              <small v-else-if="form.inputType === 'textarea'" class="text-muted">A multi-line text box.</small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Scope">
              <b-form-select v-model="form.scope" :options="scopeOptions" :disabled="locked" />
              <small class="text-muted">
                Account questions go into a form section; person questions are asked of each person of that type.
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group v-if="showOptions" label="Options">
          <b-form-textarea v-model="form.optionsText" rows="5" placeholder="One option per line" />
          <small class="text-muted">One option per line, in the order shown to the client.</small>
        </b-form-group>

        <b-row>
          <b-col v-if="isAccountScope" cols="12" md="6">
            <b-form-group label="Default section">
              <v-select
                v-model="form.defaultSectionKey"
                :options="accountSections"
                label="Title"
                :reduce="s => s.Key"
                placeholder="Pick a section"
              >
                <template #option="s">
                  <code class="mr-50">{{ s.Key }}</code>{{ s.Title }}
                </template>
              </v-select>
              <small class="text-muted">Where the question is placed when added to a package.</small>
            </b-form-group>
          </b-col>
          <b-col cols="12" :md="isAccountScope ? 6 : 12">
            <b-form-group label="Default required">
              <b-form-checkbox v-model="form.defaultRequired" switch class="mt-50">
                Required by default when added to a package
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Width (columns, 1-12)">
              <b-form-input v-model.number="form.cols" type="number" min="1" max="12" step="1" />
            </b-form-group>
          </b-col>
          <b-col v-if="form.inputType === 'textarea'" cols="12" md="6">
            <b-form-group label="Rows">
              <b-form-input v-model.number="form.rows" type="number" min="1" max="30" step="1" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group>
          <b-form-checkbox v-model="form.hasCondition">
            Only show or require this question depending on another answer
          </b-form-checkbox>
        </b-form-group>
        <b-card v-if="form.hasCondition" border-variant="light" class="mb-1 shadow-none">
          <b-row>
            <b-col cols="12" md="5">
              <b-form-group label="Only when">
                <v-select
                  v-model="form.conditionWhen"
                  :options="conditionQuestions"
                  label="Label"
                  :reduce="q => q.DataKey"
                  placeholder="Pick a question"
                >
                  <template #option="q">
                    {{ q.Label }} <small class="text-muted">{{ q.DataKey }}</small>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="is one of">
                <b-form-input v-model="form.conditionIn" placeholder="Yes, Maybe" />
                <small class="text-muted">Comma-separated values.</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="then this question is">
                <b-form-select v-model="form.conditionThen" :options="conditionThenOptions" />
              </b-form-group>
            </b-col>
          </b-row>
          <small class="text-muted">
            Otherwise the question behaves as configured above. Pick the question whose answer decides.
          </small>
        </b-card>

        <template v-if="form.id">
          <hr />
          <b-form-group>
            <b-form-checkbox v-model="form.applyToExisting">
              Apply this change to existing onboardings
            </b-form-checkbox>
            <small class="text-muted">
              Updates the question on the {{ plural(existingOnboardingCount, 'onboarding') }} already under way,
              including forms clients have already signed.
            </small>
          </b-form-group>
          <b-form-group v-if="form.applyToExisting">
            <b-form-checkbox v-model="form.notify">
              Email affected clients
            </b-form-checkbox>
          </b-form-group>
        </template>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal, BOverlay, BForm, BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormCheckbox,
  BRow, BCol, BAlert, BCard,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import {
  apiError, plural, hasOptions, linesToArray, arrayToLines, csvToArray,
} from './builderHelpers'

function emptyForm() {
  return {
    id: null,
    key: '',
    label: '',
    help: '',
    placeholder: '',
    inputType: 'text',
    optionsText: '',
    scope: 'Account',
    defaultSectionKey: null,
    defaultRequired: true,
    cols: 12,
    rows: 3,
    hasCondition: false,
    conditionWhen: null,
    conditionIn: '',
    conditionThen: 'hidden',
    isSystem: false,
    inUse: false,
    usageCount: 0,
    applyToExisting: false,
    notify: false,
  }
}

export default {
  name: 'QuestionModal',

  components: {
    BModal, BOverlay, BForm, BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormCheckbox,
    BRow, BCol, BAlert, BCard, vSelect,
  },

  props: {
    // The builder catalogue: sections, questions, scopes, inputTypes, existingOnboardingCount.
    data: { type: Object, required: true },
  },

  data() {
    return {
      show: false,
      saving: false,
      form: emptyForm(),
      conditionThenOptions: [
        { value: 'hidden', text: 'hidden' },
        { value: 'optional', text: 'optional' },
      ],
    }
  },

  computed: {
    locked() {
      return !!(this.form.id && (this.form.isSystem || this.form.inUse))
    },
    isAccountScope() {
      return !this.form.scope || this.form.scope === 'Account'
    },
    showOptions() {
      return hasOptions(this.form.inputType)
    },
    inputTypeOptions() {
      const types = (this.data.inputTypes || []).map(t => (
        typeof t === 'string' ? { value: t, text: t } : { value: t.value, text: t.label || t.value }
      ))
      // builtin fields are seeded by the system; they can never be chosen for a new question,
      // but an existing builtin question must still display its (locked) type.
      if (this.form.inputType === 'builtin' && !types.some(t => t.value === 'builtin')) {
        types.push({ value: 'builtin', text: 'builtin' })
      }
      return types.filter(t => t.value !== 'builtin' || this.form.inputType === 'builtin')
    },
    scopeOptions() {
      return (this.data.scopes || []).map(s => ({ value: s.value, text: s.label }))
    },
    accountSections() {
      return (this.data.sections || []).filter(s => !s.PersonType)
    },
    conditionQuestions() {
      return (this.data.questions || []).filter(q => q.Scope === 'Account' && q.DataKey && q.id !== this.form.id)
    },
    existingOnboardingCount() {
      return this.data.existingOnboardingCount || 0
    },
  },

  watch: {
    'form.scope': function onScope(scope) {
      if (scope && scope !== 'Account') this.form.defaultSectionKey = null
    },
  },

  methods: {
    plural,

    // open(null, { scope, defaultSectionKey }) for a new question, open(question) to edit.
    open(question, presets) {
      const form = emptyForm()
      if (question) {
        form.id = question.id
        form.key = question.Key || ''
        form.label = question.Label || ''
        form.help = question.Help || ''
        form.placeholder = question.Placeholder || ''
        form.inputType = question.InputType || 'text'
        form.optionsText = arrayToLines(question.Options)
        form.scope = question.Scope || 'Account'
        form.defaultSectionKey = question.DefaultSectionKey || null
        form.defaultRequired = question.DefaultRequired === undefined || question.DefaultRequired === null
          ? true : !!question.DefaultRequired
        form.cols = Number(question.Cols) || 12
        form.rows = Number(question.Rows) || 3
        form.isSystem = !!question.IsSystem
        form.inUse = !!question.InUse
        form.usageCount = question.UsageCount || 0
        const cond = question.Condition
        if (cond && cond.when) {
          form.hasCondition = true
          form.conditionWhen = cond.when
          form.conditionIn = (cond.in || []).join(', ')
          form.conditionThen = cond.then || 'hidden'
        }
      } else if (presets) {
        if (presets.scope) form.scope = presets.scope
        if (presets.defaultSectionKey) form.defaultSectionKey = presets.defaultSectionKey
      }
      this.form = form
      this.show = true
    },

    validate() {
      const f = this.form
      if (!f.label.trim()) return 'Label is required.'
      if (!f.inputType) return 'Pick an input type.'
      if (!f.id && f.inputType === 'builtin') return 'Built-in fields cannot be created here.'
      if (!f.scope) return 'Pick a scope.'
      if (this.showOptions && !linesToArray(f.optionsText).length) return 'Enter at least one option.'
      const cols = Number(f.cols)
      if (!cols || cols < 1 || cols > 12) return 'Width must be between 1 and 12.'
      if (f.hasCondition) {
        if (!f.conditionWhen) return 'Pick the question the condition depends on.'
        if (!csvToArray(f.conditionIn).length) return 'Enter at least one value for the condition.'
      }
      return null
    },

    handleSave(evt) {
      evt.preventDefault()
      const problem = this.validate()
      if (problem) {
        this.$bvToast.toast(problem, { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      const f = this.form
      const payload = {
        id: f.id || undefined,
        label: f.label.trim(),
        help: f.help.trim() || null,
        placeholder: f.placeholder.trim() || null,
        inputType: f.inputType,
        options: this.showOptions ? linesToArray(f.optionsText) : [],
        scope: f.scope,
        defaultSectionKey: this.isAccountScope ? (f.defaultSectionKey || null) : null,
        defaultRequired: !!f.defaultRequired,
        condition: f.hasCondition
          ? { when: f.conditionWhen, in: csvToArray(f.conditionIn), then: f.conditionThen || 'hidden' }
          : null,
        cols: Number(f.cols) || 12,
        rows: f.inputType === 'textarea' ? (Number(f.rows) || 3) : null,
        applyToExisting: !!f.id && !!f.applyToExisting,
        notify: !!f.id && !!f.applyToExisting && !!f.notify,
      }
      this.saving = true
      store.dispatch('settings/saveOnboardingQuestion', payload)
        .then(res => {
          this.$bvToast.toast(res.data.message || 'Question saved.', { variant: 'success', title: 'Success', solid: true })
          this.show = false
          this.$emit('saved', res.data.question || null)
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.saving = false })
    },
  },
}
</script>
